import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { cn } from "@/lib/utils";
import { Plus, Trash2, ExternalLink, Copy } from "lucide-react";

interface SmartLink {
  id: number;
  name: string;
  status: string;
  routingType: string;
  catchAllOfferId: number | null;
  description: string | null;
  createdAt: string;
}

export function SmartLinksPage() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", routingType: "priority", catchAllOfferId: "", description: "" });
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const { data: linksData, isLoading } = useQuery({
    queryKey: ["smart-links"],
    queryFn: async () => {
      const res = await api.get("/v1/smart-links");
      if (!res.ok) throw new Error("Failed to load smart links");
      return res.json() as Promise<{ data: SmartLink[] }>;
    },
  });

  const { data: offersData } = useQuery({
    queryKey: ["offers-list-all"],
    queryFn: async () => {
      const res = await api.get("/v1/offers", { pageSize: "500" });
      if (!res.ok) return { data: [] };
      return res.json() as Promise<{ data: Array<{ id: number; name: string }> }>;
    },
    staleTime: 60_000,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post("/v1/smart-links", {
        name: form.name,
        routingType: form.routingType,
        catchAllOfferId: form.catchAllOfferId ? Number(form.catchAllOfferId) : undefined,
        description: form.description || undefined,
      });
      if (!res.ok) throw new Error("Failed to create smart link");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["smart-links"] });
      setShowCreate(false);
      setForm({ name: "", routingType: "priority", catchAllOfferId: "", description: "" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await api.delete(`/v1/smart-links/${id}`);
      if (!res.ok) throw new Error("Failed to delete");
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["smart-links"] }),
  });

  const links = linksData?.data ?? [];
  const offers = offersData?.data ?? [];

  return (
    <div>
      <PageHeader title="Smart Links" description="Route traffic to the best matching offer based on targeting rules" />

      <div className="flex justify-end mb-6">
        <button onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800">
          <Plus className="w-4 h-4" /> New Smart Link
        </button>
      </div>

      {showCreate && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Create Smart Link</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input type="text" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="US Mobile Offers" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Routing Type</label>
              <select value={form.routingType} onChange={(e) => setForm((f) => ({ ...f, routingType: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm">
                <option value="priority">Priority (highest priority wins)</option>
                <option value="weight">Weighted (random by weight)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Catch-All Offer</label>
              <select value={form.catchAllOfferId} onChange={(e) => setForm((f) => ({ ...f, catchAllOfferId: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm">
                <option value="">None</option>
                {offers.map((o) => <option key={o.id} value={String(o.id)}>{o.name} (#{o.id})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input type="text" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={() => createMutation.mutate()} disabled={!form.name || createMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
              {createMutation.isPending ? "Creating..." : "Create"}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
          </div>
        ) : links.length === 0 ? (
          <div className="p-12 text-center text-gray-500 text-sm">No smart links yet</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Name</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Routing</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Catch-All</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {links.map((link) => (
                <tr key={link.id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedId(selectedId === link.id ? null : link.id)}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{link.name}</td>
                  <td className="px-4 py-3">
                    <span className={cn("px-2 py-0.5 rounded text-xs font-medium",
                      link.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                    )}>{link.status}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 capitalize">{link.routingType}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{link.catchAllOfferId ? `#${link.catchAllOfferId}` : "-"}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setSelectedId(link.id)} className="p-1 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-700" title="Manage rules">
                        <ExternalLink className="w-4 h-4" />
                      </button>
                      <button onClick={() => { if (confirm("Delete this smart link?")) deleteMutation.mutate(link.id); }}
                        className="p-1 rounded hover:bg-red-100 text-gray-500 hover:text-red-700" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId && <SmartLinkDetail id={selectedId} offers={offers} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

function SmartLinkDetail({ id, offers, onClose }: { id: number; offers: Array<{ id: number; name: string }>; onClose: () => void }) {
  const qc = useQueryClient();
  const [addRule, setAddRule] = useState(false);
  const [ruleForm, setRuleForm] = useState({ offerId: "", priority: "0", weight: "100", countries: "", platforms: "", deviceTypes: "" });

  const { data: detail } = useQuery({
    queryKey: ["smart-link", id],
    queryFn: async () => {
      const res = await api.get(`/v1/smart-links/${id}`);
      if (!res.ok) throw new Error("Failed to load");
      return res.json() as Promise<{ data: any }>;
    },
  });

  const addRuleMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        offerId: Number(ruleForm.offerId),
        priority: Number(ruleForm.priority),
        weight: Number(ruleForm.weight),
      };
      if (ruleForm.countries.trim()) payload.countries = ruleForm.countries.split(",").map((s) => s.trim()).filter(Boolean);
      if (ruleForm.platforms.trim()) payload.platforms = ruleForm.platforms.split(",").map((s) => s.trim()).filter(Boolean);
      if (ruleForm.deviceTypes.trim()) payload.deviceTypes = ruleForm.deviceTypes.split(",").map((s) => s.trim()).filter(Boolean);
      const res = await api.post(`/v1/smart-links/${id}/rules`, payload);
      if (!res.ok) throw new Error("Failed to add rule");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["smart-link", id] });
      setAddRule(false);
      setRuleForm({ offerId: "", priority: "0", weight: "100", countries: "", platforms: "", deviceTypes: "" });
    },
  });

  const deleteRuleMutation = useMutation({
    mutationFn: async (ruleId: number) => {
      const res = await api.delete(`/v1/smart-links/${id}/rules/${ruleId}`);
      if (!res.ok) throw new Error("Failed to delete rule");
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["smart-link", id] }),
  });

  const link = detail?.data;
  const rules = link?.rules ?? [];
  const trackingUrl = link ? `[tracking-domain]/smartlink/${link.networkId}/${link.id}/[affiliate_id]` : "";

  return (
    <div className="mt-6 bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{link?.name ?? "Smart Link"} — Rules</h3>
        <div className="flex items-center gap-2">
          <button onClick={() => { navigator.clipboard.writeText(trackingUrl); }}
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700" title="Copy tracking URL">
            <Copy className="w-3.5 h-3.5" /> URL
          </button>
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">Close</button>
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-4 font-mono bg-gray-50 px-3 py-2 rounded">{trackingUrl}</p>

      <div className="flex justify-end mb-3">
        <button onClick={() => setAddRule(true)}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800">
          <Plus className="w-4 h-4" /> Add Rule
        </button>
      </div>

      {addRule && (
        <div className="border border-gray-200 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Offer *</label>
              <select value={ruleForm.offerId} onChange={(e) => setRuleForm((f) => ({ ...f, offerId: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm">
                <option value="">Select offer</option>
                {offers.map((o) => <option key={o.id} value={String(o.id)}>{o.name} (#{o.id})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
              <input type="number" value={ruleForm.priority} onChange={(e) => setRuleForm((f) => ({ ...f, priority: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Weight</label>
              <input type="number" value={ruleForm.weight} onChange={(e) => setRuleForm((f) => ({ ...f, weight: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Countries (comma-sep)</label>
              <input type="text" value={ruleForm.countries} onChange={(e) => setRuleForm((f) => ({ ...f, countries: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm" placeholder="US,CA,GB" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Platforms (comma-sep)</label>
              <input type="text" value={ruleForm.platforms} onChange={(e) => setRuleForm((f) => ({ ...f, platforms: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm" placeholder="iOS,Android,Windows" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Device Types (comma-sep)</label>
              <input type="text" value={ruleForm.deviceTypes} onChange={(e) => setRuleForm((f) => ({ ...f, deviceTypes: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm" placeholder="mobile,desktop,tablet" />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-3">
            <button onClick={() => setAddRule(false)} className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={() => addRuleMutation.mutate()} disabled={!ruleForm.offerId || addRuleMutation.isPending}
              className="px-3 py-1.5 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
              {addRuleMutation.isPending ? "Adding..." : "Add Rule"}
            </button>
          </div>
        </div>
      )}

      {rules.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">No rules configured yet. Add rules to start routing traffic.</p>
      ) : (
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-3 py-2">Offer</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-3 py-2">Priority</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-3 py-2">Weight</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-3 py-2">Countries</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-3 py-2">Platforms</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-3 py-2">Devices</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-3 py-2">Status</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rules.map((rule: any) => {
              const offerName = offers.find((o) => o.id === rule.offerId)?.name || `#${rule.offerId}`;
              const countries = typeof rule.countries === "string" ? JSON.parse(rule.countries || "[]") : (rule.countries || []);
              const platforms = typeof rule.platforms === "string" ? JSON.parse(rule.platforms || "[]") : (rule.platforms || []);
              const deviceTypes = typeof rule.deviceTypes === "string" ? JSON.parse(rule.deviceTypes || "[]") : (rule.deviceTypes || []);
              return (
                <tr key={rule.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2 text-sm">{offerName}</td>
                  <td className="px-3 py-2 text-sm">{rule.priority}</td>
                  <td className="px-3 py-2 text-sm">{rule.weight}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{countries.length > 0 ? countries.join(", ") : "Any"}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{platforms.length > 0 ? platforms.join(", ") : "Any"}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{deviceTypes.length > 0 ? deviceTypes.join(", ") : "Any"}</td>
                  <td className="px-3 py-2">
                    <span className={cn("px-2 py-0.5 rounded text-xs font-medium",
                      rule.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                    )}>{rule.status}</span>
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => { if (confirm("Delete this rule?")) deleteRuleMutation.mutate(rule.id); }}
                      className="p-1 rounded hover:bg-red-100 text-gray-500 hover:text-red-700">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
